import { lazy, Suspense, useEffect, useState } from 'react';

import { FlowProvider } from '@sunflower-games/flow-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { useEffectOnce } from 'react-use';

import './i18n/config';

import { useInactivityReload } from './hooks/useInactivityReload';
import Casino from './pages/Casino';
import Layout from './pages/Layout';
import Lobby from './pages/Lobby';
import MaintenancePage from './pages/Maintenance/Maintenance';
import { registerAuthComponents } from './services/authRegistery';
import { useFlag } from './services/launchDarkly';
import { startShellBridge } from './services/nativeBridge/shellBridge';
import { queryClient } from './services/queryClient';
import { APP_MOUNT_DATE } from './utils/appMount';
import { isProduction } from './utils/getEnvironment';
import { AUTH_ACTION_PATH } from './utils/isExemptPage';
import { loadScript } from './utils/loadScript';

const flowUrl = import.meta.env.VITE_FLOW_URL;

// Before any route renders: Home asks for the post sign-up wizard before Lobby's effects run, and a
// direct /games/:id landing never mounts Lobby at all (GS-871).
registerAuthComponents();

// ❗️[Layout, Lobby and Casino] must be imported without lazy, because they are most likely to be used first
const Game = lazy(() => import('./pages/Game/Game'));
const Bingo = lazy(() => import('./pages/Bingo/BingoPage'));
const NotFound = lazy(() => import('./pages/errors/NotFound'));
const CMSPage = lazy(() => import('./pages/CMS/Pages'));
const FootballPromoTerms = lazy(() =>
  import('./pages/FootballPromoTerms/FootballPromoTerms').then((m) => ({ default: m.FootballPromoTerms })),
);
const DepositCashDemo = lazy(() => import('./pages/DepositCashDemo/DepositCashDemo'));
const CrownRipsEntry = lazy(() => import('./pages/CrownRipsEntry'));
const AuthAction = lazy(() => import('./pages/AuthAction/AuthAction'));

const rootRouter = createBrowserRouter([
  // Firebase email-action links (verify email, reset password). Outside Layout: no stores, no auth init.
  {
    path: AUTH_ACTION_PATH,
    element: (
      <Suspense fallback={null}>
        <AuthAction />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        path: '/',
        element: <Casino />,
        children: [
          {
            path: '/',
            element: <Lobby />,
          },
          {
            path: '/games/:gameId',
            element: <Game />,
          },
          {
            path: '/bingo',
            element: <Bingo />,
          },
          {
            path: '/crown-rips',
            element: <CrownRipsEntry />,
          },
        ],
      },
      {
        path: '/pages/:pageSlug',
        element: <CMSPage />,
      },
      {
        path: '/matchday-giveaway-terms',
        element: <FootballPromoTerms />,
      },
      ...(isProduction
        ? []
        : [
            {
              path: '/deposit-cash-demo',
              element: <DepositCashDemo />,
            },
          ]),
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]);

export default function App() {
  const isMaintenance = useFlag<boolean>('maintenance', false);
  const isFlowEnabled = useFlag<boolean>('flow_kill_switch', false);
  const isFlowDebugEnabled = useFlag<boolean>('flow_debug_switch', false);

  const isAccessibilityEnabled = useFlag<boolean>('accessibility_enabled', false);

  const [bypassMaintenance, setBypassMaintenance] = useState(false);

  useInactivityReload();

  useEffect(() => {
    if (!isAccessibilityEnabled) return;
    if (document.querySelector('script[src="/vendor/accessibility.js"]')) return;
    void loadScript('/vendor/accessibility.js', {
      defer: true,
    });
  }, [isAccessibilityEnabled]);

  useEffectOnce(() => {
    // A no-op outside the mobile shell. From a mount effect rather than module scope — see `shellBridge`.
    // Its links are routed by `Layout` (`useShellDeepLinks`), which holds the stores.
    startShellBridge();

    window.addEventListener('message', (event) => {
      if (event.data === 'bypass-maintenance') {
        setBypassMaintenance(true);
      }
    });

    const sessionStartDate = sessionStorage.getItem('session-start-date');
    if (!sessionStartDate) {
      sessionStorage.setItem('session-start-date', APP_MOUNT_DATE);
    }
  });

  if (isMaintenance && !bypassMaintenance) {
    return <MaintenancePage />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <FlowProvider baseUrl={flowUrl} debug={isFlowDebugEnabled} enabled={isFlowEnabled}>
          <RouterProvider router={rootRouter} />
        </FlowProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}
